import { TEXTOS } from '../../lib/glossario'
import { competenciaPorExtenso } from '../chamados/competencia'
import type { LinhaDaSerie } from '../../types/indicadores'
import { TETO_DO_CONTRATO, alturaDaBarra } from './serie'

/**
 * O desconto de cada mês, em barras.
 *
 * **A escala é do contrato, não do dado.** O topo é sempre o teto de 10% do item h (p. 148). Uma
 * escala que se ajustasse ao maior valor da série faria um mês de 2% parecer alto num contrato bem
 * executado e baixo num mal executado — e a pergunta que esta tela responde é justamente "estamos
 * perto do teto?".
 *
 * **Barra em CSS, e não biblioteca de gráfico.** São doze retângulos com uma altura calculada; uma
 * dependência nova para isso traria peso, superfície e uma decisão de arquitetura que esta tela não
 * precisa tomar. O número vai escrito ao lado da barra, e a tabela abaixo tem tudo — a barra é
 * complemento, nunca a única forma de ler o dado.
 *
 * @param props a série, da mais recente para a mais antiga
 * @returns as barras, da mais antiga para a mais recente
 */
export function EvolucaoDoDesconto({ linhas }: { linhas: LinhaDaSerie[] }) {
  const t = TEXTOS.indicadores
  const comNumero = [...linhas].reverse().filter((l) => l.redutorApurado != null)

  if (comNumero.length < 2) {
    return null
  }

  return (
    <section className="cartao p-4" aria-label={t.evolucao.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.evolucao.titulo}</h2>
      <p className="mt-1 max-w-prose text-sm text-slate-600">{t.evolucao.ajuda}</p>

      <ol className="mt-4 flex items-end gap-2 overflow-x-auto pb-1">
        {comNumero.map((linha) => (
          <li key={linha.competencia} className="flex min-w-16 flex-1 flex-col items-center gap-1">
            <span className="text-xs font-semibold tabular-nums text-slate-900">−{linha.redutorApurado}%</span>
            <div className="flex h-24 w-full items-end rounded bg-slate-100" aria-hidden="true">
              {/* Única altura calculada em `style` da tela: a proporção sai de um número que só
                  existe em tempo de execução, e classe Tailwind não a expressa. O valor vai escrito
                  acima da barra, e o `role=img` abaixo carrega a leitura acessível. */}
              <div
                className={linha.redutorApurado === TETO_DO_CONTRATO ? 'w-full rounded bg-red-500' : 'w-full rounded bg-sky-600'}
                style={{ height: `${alturaDaBarra(linha.redutorApurado ?? 0)}%` }}
              />
            </div>
            <span
              role="img"
              aria-label={t.evolucao.leitura(competenciaPorExtenso(linha.competencia), linha.redutorApurado ?? 0)}
              className="text-center text-xs text-slate-600"
            >
              {linha.competencia}
            </span>
          </li>
        ))}
      </ol>

      <p className="mt-2 text-xs text-slate-500">{t.evolucao.escala(TETO_DO_CONTRATO)}</p>
    </section>
  )
}
